import { useCallback, useEffect, useRef, useState } from 'react'

type SetValueInput<T> = T | ((arg0: T) => T)
type UseLocalStorageReturn<T> = [T, (arg0: SetValueInput<T>) => void]

export const LOCAL_STORAGE_CHANGE_EVENT = 'forumm:local-storage-change'

type LocalStorageChangeDetail<T> = {
  key: string
  value: T
}

export default function useLocalStorage<T>(
  key: string,
  initialValue: T
): UseLocalStorageReturn<T> {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue
    }
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      return initialValue
    }
  })
  const initialValueRef = useRef(initialValue)
  const storedValueRef = useRef(storedValue)

  useEffect(() => {
    initialValueRef.current = initialValue
  }, [initialValue])

  useEffect(() => {
    storedValueRef.current = storedValue
  }, [storedValue])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const setCurrentValue = (value: T) => {
      storedValueRef.current = value
      setStoredValue(value)
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== key) return

      if (event.newValue === null) {
        setCurrentValue(initialValueRef.current)
        return
      }

      try {
        setCurrentValue(JSON.parse(event.newValue))
      } catch (_error) {
        setCurrentValue(initialValueRef.current)
      }
    }

    const handleLocalStorageChange = (event: Event) => {
      const detail = (event as CustomEvent<LocalStorageChangeDetail<T>>).detail
      if (detail?.key !== key) return
      setCurrentValue(detail.value)
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener(
      LOCAL_STORAGE_CHANGE_EVENT,
      handleLocalStorageChange
    )

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(
        LOCAL_STORAGE_CHANGE_EVENT,
        handleLocalStorageChange
      )
    }
  }, [key])

  const setValue = useCallback((value: SetValueInput<T>) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValueRef.current) : value
      storedValueRef.current = valueToStore
      setStoredValue(valueToStore)
      if (typeof window !== 'undefined') {
        if (valueToStore === undefined) {
          window.localStorage.removeItem(key)
        } else {
          window.localStorage.setItem(key, JSON.stringify(valueToStore))
        }

        window.dispatchEvent(
          new CustomEvent<LocalStorageChangeDetail<T>>(
            LOCAL_STORAGE_CHANGE_EVENT,
            { detail: { key, value: valueToStore } }
          )
        )
      }
    } catch (error) {
      console.error(error)
    }
  }, [key])

  return [storedValue, setValue]
}
